import { Dropdown, Field, Option, makeStyles } from '@fluentui/react-components';
import { useQuery } from '@tanstack/react-query';

import { getMigrationGroups } from '../api/migrationGroups';

// The single group-selection control, used on the create form, on the migration
// detail header, and in the group page's add-migrations dialog.
//
// ⚠ IT EXPOSES NO `required` PROP, AND THAT IS DELIBERATE.
//
// FD-254: `TenantResolveField` hardcoded `required`, which was correct while
// every use was mandatory. The first optional reuse silently stopped the create
// form submitting — button enabled, click landed, nothing happened — because an
// empty *required* input fails native form validation and the submit event never
// fires. Nobody could see it, because the failure is the ABSENCE of an event.
//
// Group membership is optional everywhere and always will be: FD-ADR-030 makes
// "no group" the default state of most migrations. A prop that does not exist
// cannot be hardcoded wrong, so the safest design here is not to offer one.

export const NO_GROUP_VALUE = '__none__';

const useStyles = makeStyles({
  hint: {
    fontSize: '12px',
    color: '#71717a',
    marginTop: '4px',
  },
});

export interface GroupPickerProps {
  // `null` means "no group" — the ordinary state, not an error.
  value: string | null;
  onChange: (migrationGroupId: string | null) => void;
  label?: string;
  hint?: string;
  disabled?: boolean;
}

export default function GroupPicker({
  value,
  onChange,
  label = 'Group',
  hint,
  disabled,
}: GroupPickerProps) {
  const styles = useStyles();

  const query = useQuery({
    queryKey: ['migration-groups'],
    queryFn: getMigrationGroups,
  });

  const groups = query.data?.groups ?? [];
  const selected = value ?? NO_GROUP_VALUE;
  const selectedGroup = groups.find((g) => g.migrationGroupId === value);

  // Three display states, and the ORDER matters:
  //
  //   value + resolved   → the group's name
  //   value + NOT loaded → "…" — NOT the raw id. Falling back to the id while
  //                        the list is still loading would flash a bare GUID on
  //                        every grouped migration, visually identical to the
  //                        dangling state below. (Caught by a test that raced
  //                        the query rather than by inspection.)
  //   value + loaded but unresolved → the raw id: the group was deleted, and
  //                        that is a real state an operator needs to see.
  const displayValue = !value
    ? 'No group'
    : selectedGroup
      ? selectedGroup.name
      : query.isLoading
        ? '…'
        : value;

  return (
    <Field label={label}>
      <Dropdown
        value={displayValue}
        selectedOptions={[selected]}
        disabled={disabled || query.isLoading}
        onOptionSelect={(_, data) => {
          const next = data.optionValue;
          onChange(!next || next === NO_GROUP_VALUE ? null : next);
        }}
      >
        <Option value={NO_GROUP_VALUE} text="No group">
          No group
        </Option>
        {groups.map((g) => (
          <Option key={g.migrationGroupId} value={g.migrationGroupId} text={g.name}>
            {g.name}
          </Option>
        ))}
      </Dropdown>
      {hint && <div className={styles.hint}>{hint}</div>}
    </Field>
  );
}
